import { Text } from "@mantine/core";
import { getCellWidth } from "src/generator/utils/getCellWidth";
import {
  getHiddenCellBorderFlags,
  isBlankCell,
} from "src/generator/PortraitOrientation/Row/hiddenCellBorders";
import { useShowContent } from "src/generator/hooks/useShowContent";
import { StyledTd2 } from "src/styled";

import useCellProps from "../../../../generator/PortraitOrientation/Row/useCellProps";
import { useTheme } from "../../../../hooks";
import { type ICol } from "../../../../interfaces";
import { useSelector } from "../../../../redux/hooks";

import ValueRenderer from "./ValueRenderer";
interface Props {
  col: ICol;
  index: number;
  isOverlay?: boolean;
  isDraftEntry?: boolean;
  history?: boolean;
  rowIndex?: number;
  lbeid?: string;
  entryIndex?: number;
  span?: number;
  pdfEntry?: boolean;
  layout?: number[];
  subRowIndex?: number;
  rowsLength?: number;
  siblingRows?: any[];
  rowIndexForBorders?: number;
  columnOffset?: number;
}
const RowCell = ({
  col,
  index,
  isOverlay,
  isDraftEntry,
  history,
  rowIndex,
  rowsLength,
  lbeid,
  entryIndex,
  span,
  pdfEntry,
  layout,
  subRowIndex,
  siblingRows,
  rowIndexForBorders,
  columnOffset,
}: Props) => {
  const theme = useTheme() as any;
  const { isDarkMode } = useSelector(state => state.preferencesSlice);
  const { shipType } = useSelector(state => state.generatorSlice);
  const type = useSelector(
    state => state.entriesGeneratorSlice.reportDetails?.type,
  );
  const { hiddenStyles } = useCellProps(Boolean(isOverlay));
  const { shouldHideElement } = useShowContent();
  const colSpan =
    col && span ? span : col.colspan ? col.colspan : !col.full ? 1 : 999;
  const shouldBeGrayed =
    (shipType === "1" && (col as any).class === "grp-pass") || col.grayed;

  if (col.unit === "space" || col.type === "space") {
    const useMargin =
      subRowIndex !== undefined &&
      rowIndex !== undefined &&
      rowsLength !== undefined &&
      rowIndex !== rowsLength - 1;

    return (
      <td
        className="border-none"
        colSpan={colSpan}
        style={{
          backgroundColor: theme.colors.background[0],
          border: "none",
          height: useMargin ? 10 : 0,
          minWidth: layout ? undefined : getCellWidth(col.width),
        }}
      />
    );
  }

  if (col.hidden) {
    const { showTopBorder, showBottomBorder, showLeftBorder, showRightBorder } =
      getHiddenCellBorderFlags(
        siblingRows ?? [],
        rowIndexForBorders ?? -1,
        columnOffset ?? 0,
        colSpan,
        shouldHideElement,
      );

    return (
      <StyledTd2
        colSpan={colSpan}
        showTopBorder={showTopBorder}
        showBottomBorder={showBottomBorder}
        showLeftBorder={showLeftBorder}
        showRightBorder={showRightBorder}
        isBlankSpacer={isBlankCell(col)}
        height={col.height}
        style={{ minWidth: getCellWidth(col.width) }}
      />
    );
  }
  return (
    <td
      id={col.id}
      colSpan={colSpan}
      rowSpan={col.rowspan ?? 1}
      key={index}
      style={{
        border: `1px solid ${history && col.unit !== "steering.pumps"
          ? isDarkMode
            ? "#31383f"
            : "rgb(190, 190, 190)"
          : theme.colors.main[9]
          }`,
        ...(history && { borderBottom: "none" }),
        textAlign: col.align ?? "center",
        ...(type !== 3 && {
          padding: col.unit === "check" ? "6px" : "8px",
        }),
        position: "relative",
        opacity: (history ?? isDraftEntry) ? 0.2 : 1,
        backgroundColor: shouldBeGrayed ? theme.colors.grayscale[2] : "",
        ...(col.background === "dimmed" && {
          backgroundColor: theme.colors.tdBackgroundColor[1],
        }),
        textDecoration:
          history && col.unit !== "steering.pumps" ? "line-through" : "none",
        ...((col.unit === "large/text" || col.unit === "text") && {
          maxWidth: "400px",
        }),
        ...hiddenStyles,
        minWidth: layout ? undefined : getCellWidth(col.width),
        width: layout
          ? undefined
          : col.explicitWidth
            ? getCellWidth(col.width)
            : "auto",
      }}>
      {(() => {
        const hasCellAffix = Boolean(col.cellPrefix || col.cellSuffix);
        const valueNode = (
          <ValueRenderer
            col={col}
            rowIndex={rowIndex}
            colIndex={index}
            lbeid={lbeid}
            entryIndex={entryIndex}
            pdfEntry={pdfEntry}
          />
        );

        return (
          <div
            style={{
              minHeight: "22px",
              display: "flex",
              justifyContent: hasCellAffix
                ? "space-between"
                : (col.align ?? col.list)
                  ? "flex-start"
                  : col.suffix && !col.value
                    ? "flex-end"
                    : col.unit === "BLANK"
                      ? "flex-start"
                      : col.unit === "check"
                        ? "center"
                        : "flex-start", // align to left same as entry
              alignItems: "center",
              opacity: col.suffix && !col.value && !col.list ? 0.6 : 1,
              color: col.changed
                ? col.changedType === "edit"
                  ? "red"
                  : "green"
                : "auto",
              ...hiddenStyles,
              ...(col.minHeight && {
                minHeight: col.minHeight,
                height: "auto",
              }),
              ...((col.unit === "large/text" || col.unit === "text") && {
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }),
              ...((col.cellPrefix || col.cellSuffix) && { gap: 7 }),
            }}>
            {col.cellPrefix && <Text c="text" size="sm">{col.cellPrefix}</Text>}
            {hasCellAffix ? (
              <div style={{ flex: 1, textAlign: "center" }}>{valueNode}</div>
            ) : (
              valueNode
            )}
            {col.cellSuffix && <Text c="text" size="sm">{col.cellSuffix}</Text>}
          </div>
        );
      })()}
      {history && col.unit === "check" && (
        <div
          style={{
            position: "absolute",
            height: "0.5px",
            width: "24px",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            background: "black",
          }}
        />
      )}
    </td>
  );
};
export default RowCell;
